/**
 * google-signin.ts — loads Google Identity Services (GIS) for "Continue with
 * Google" sign-in. This is the ID-token flow (authentication), separate from the
 * Drive OAuth token flow in google-drive.ts — but both use the same public
 * GOOGLE_CLIENT_ID and the same GIS script, so the origin allow-list already
 * covers app.editlock.io + localhost:5173.
 *
 * The returned credential is a Google-signed JWT (ID token); the worker verifies
 * it at POST /auth/google against Google's JWKS. Nothing secret lives here.
 */

let gisReady: Promise<void> | null = null;

/** Idempotent, promise-cached load of the GIS client script. */
export function loadGoogleIdentity(): Promise<void> {
  if (gisReady) return gisReady;
  gisReady = new Promise<void>((resolve, reject) => {
    // Already present (e.g. Drive import loaded it first)?
    if ((window as any).google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Google sign-in")));
      return;
    }
    const el = document.createElement("script");
    el.src = "https://accounts.google.com/gsi/client";
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("Failed to load Google sign-in"));
    document.head.appendChild(el);
  });
  return gisReady;
}
